import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contactsService } from '../services/contacts.service';
import { colocService, Coloc } from '../services/coloc.service';
import { AgencyOverview } from '../types/contact';
import { getErrorMessage } from '../utils/error';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';
import '../styles/TenantsPage.css';

/**
 * Fiche d'une agence : ses colocations, son accès au compte, ses emails en copie.
 *
 * PAGE et non repli. Le détail entassé dans une ligne de tableau a coûté quatre
 * défauts sur cet écran — voir `AgencesPage`. Ici chaque bloc a la largeur de la
 * page, l'URL est partageable, et le retour est un vrai retour.
 *
 * Aucune route dédiée côté API : `GET /api/agencies` renvoie déjà tout, la liste
 * est courte, et une seconde route à maintenir pour un filtre `find` serait du
 * travail sans contrepartie.
 */
const AgenceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [agence, setAgence] = useState<AgencyOverview | null>(null);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [chargement, setChargement] = useState(true);
  const [introuvable, setIntrouvable] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [ajout, setAjout] = useState('');
  const [ccTexte, setCcTexte] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [liste, mesColocs] = await Promise.all([
        contactsService.listOwnerAgencies(),
        colocService.getColocs(),
      ]);
      const trouvee = liste.find((a) => a.id === id) ?? null;
      setAgence(trouvee);
      setIntrouvable(trouvee === null);
      setColocs(mesColocs);
      if (trouvee) {
        setCcTexte((await contactsService.getCcRecipients(trouvee.id)).join('\n'));
      }
      setErreur(null);
    } catch (err) {
      setErreur(getErrorMessage(err, 'Erreur lors du chargement'));
    } finally {
      setChargement(false);
    }
  }, [id]);

  useEffect(() => {
    charger();
  }, [charger]);

  const agir = async (action: () => Promise<string | null>) => {
    setOccupe(true);
    setErreur(null);
    setInfo(null);
    try {
      const message = await action();
      if (message) setInfo(message);
      await charger();
    } catch (err) {
      setErreur(getErrorMessage(err, "Erreur lors de l'opération"));
    } finally {
      setOccupe(false);
    }
  };

  if (chargement) return <div className="candidats-page"><div className="loading">Chargement...</div></div>;

  if (introuvable || !agence) {
    return (
      <div className="candidats-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Agence introuvable</p>
          <p className="socle-vide-aide">
            Elle n'existe plus, ou vous ne partagez plus aucune colocation avec elle.
          </p>
          <button className="btn-action btn-secondary" onClick={() => navigate('/agences')}>
            Retour aux agences
          </button>
        </div>
      </div>
    );
  }

  const dejaPartagees = new Set(agence.colocs.map((c) => c.colocId));
  const disponibles = colocs.filter((c) => !dejaPartagees.has(c.id));

  const handleBascule = (colocId: string, actif: boolean) =>
    agir(async () => {
      await contactsService.setAgencyAccess(colocId, agence.id, actif);
      return null;
    });

  const handleRetirer = (colocId: string, nomColoc: string) => {
    if (
      !window.confirm(
        `Retirer à ${agence.name} l'accès à ${nomColoc} ?\n\n` +
          'Les contacts encore ouverts qui lui étaient confiés vous reviendront. ' +
          "Son historique d'actions est conservé.\n\n" +
          "Pour une interruption temporaire, préférez « Suspendre » : rien n'est défait.",
      )
    ) {
      return;
    }
    return agir(async () => {
      const r = await contactsService.revokeAgency(colocId, agence.id);
      // Retirer la DERNIÈRE colocation fait disparaître l'agence de votre
      // périmètre : rester sur sa fiche afficherait « introuvable ».
      if (agence.colocs.length === 1) {
        navigate('/agences');
        return null;
      }
      return r.contactsDesassignes > 0
        ? `Accès retiré. ${r.contactsDesassignes} contact(s) vous sont rendus.`
        : 'Accès retiré.';
    });
  };

  const handleAjouter = () =>
    agir(async () => {
      const [prenomA, ...reste] = agence.name.split(' ');
      // On repasse par l'octroi : il sait réutiliser un compte existant, et
      // c'est exactement ce qu'on veut ici. Nom et prénom sont ignorés côté
      // serveur pour un compte connu.
      await contactsService.grantAgency(ajout, {
        first_name: prenomA,
        last_name: reste.join(' ') || prenomA,
        email: agence.email,
        send_welcome: false,
      });
      const nomColoc = colocs.find((c) => c.id === ajout)?.name ?? '';
      setAjout('');
      return `${agence.name} a désormais accès à ${nomColoc}.`;
    });

  const handleBienvenue = () =>
    agir(async () => {
      // L'email cite la colocation choisie : une dont l'accès est actif, pas
      // la première par ordre alphabétique, peut-être suspendue (ticket #20).
      const coloc = agence.colocs.find((c) => c.accessEnabled) ?? agence.colocs[0];
      const r = await contactsService.sendAgencyWelcome(coloc.colocId, agence.id);
      return r.sent
        ? `Email de bienvenue envoyé à ${agence.email}.`
        : `L'envoi à ${agence.email} a échoué. L'accès reste accordé — réessayez.`;
    });

  const handleCc = () =>
    agir(async () => {
      const liste = await contactsService.setCcRecipients(
        agence.id,
        ccTexte.split('\n').map((s) => s.trim()).filter(Boolean),
      );
      setCcTexte(liste.join('\n'));
      return 'Liste enregistrée.';
    });

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/agences')}
          >
            ← Agences
          </button>
          <h2>{agence.name}</h2>
          <p className="access-hint" style={{ margin: 0 }}>{agence.email}</p>
        </div>
      </div>

      {erreur && <div className="candidats-error">{erreur}</div>}
      {info && <div className="candidats-notice">{info}</div>}

      <div className="cx-detail">
        <div className="cx-card">
          <h4 className="cx-title">Colocations partagées</h4>
          <p className="access-hint">
            Suspendre gèle l'accès sans rien défaire — le rétablir retrouve tout en
            place. Retirer l'accès vous rend les contacts encore ouverts.
          </p>
          <div className="contacts-table-container">
            <table className="candidats-table">
              <thead>
                <tr><th>Colocation</th><th>Accès</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {agence.colocs.map((c) => (
                  <tr key={c.colocId}>
                    <td className="candidat-name">{c.colocName}</td>
                    <td>
                      <span className={`tenant-access-badge access-${c.accessEnabled ? 'on' : 'off'}`}>
                        {c.accessEnabled ? 'Actif' : 'Suspendu'}
                      </span>
                    </td>
                    <td className="cx-actions">
                      <button
                        className="btn-action btn-secondary btn-bascule-acces"
                        disabled={occupe}
                        onClick={() => handleBascule(c.colocId, !c.accessEnabled)}
                      >
                        {c.accessEnabled ? 'Suspendre' : 'Rétablir'}
                      </button>
                      <button
                        className="btn-action btn-danger"
                        disabled={occupe}
                        onClick={() => handleRetirer(c.colocId, c.colocName)}
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {disponibles.length > 0 && (
            <div className="cx-actions" style={{ marginTop: 16 }}>
              <select value={ajout} onChange={(e) => setAjout(e.target.value)}>
                <option value="">— Ajouter une colocation —</option>
                {disponibles.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                className="btn-action btn-save"
                disabled={occupe || !ajout}
                onClick={handleAjouter}
              >
                Ajouter
              </button>
            </div>
          )}
        </div>

        <div className="cx-card">
          <h4 className="cx-title">Accès au compte</h4>
          <p className="access-hint">
            L'email de bienvenue contient un lien pour que l'agence choisisse son
            mot de passe — vous ne le connaîtrez jamais. Renvoyez-le si le lien a
            expiré ou si l'email s'est perdu.
          </p>
          <div className="cx-actions">
            <button className="btn-action btn-save" disabled={occupe} onClick={handleBienvenue}>
              Envoyer l'email de bienvenue
            </button>
          </div>
        </div>

        <div className="cx-card">
          <h4 className="cx-title">Collègues à mettre en copie</h4>
          <p className="access-hint">
            Ces adresses sont en copie de deux emails seulement : l'assignation d'un
            contact et sa relance. Une demande envoyée à une seule personne meurt
            quand elle est en congé.
          </p>
          <div className="action-field">
            <textarea
              rows={4}
              value={ccTexte}
              onChange={(e) => setCcTexte(e.target.value)}
              placeholder="une adresse par ligne"
            />
          </div>
          <div className="cx-actions">
            <button className="btn-action btn-save" disabled={occupe} onClick={handleCc}>
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenceDetailPage;
